import React, { CSSProperties, FC, useCallback } from 'react';
import { Column } from '@tanstack/react-table';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { useDataTableContext } from '../../../utils/DataTableContext';
import { useIntlUds } from '../../../../../translations/UdsIntlProvider';
import { Dropdown } from '../../../../Dropdown';
import { MenuList, MenuListItem, MenuListItemText, MenuListLabel } from '../../../../MenuList';
import { SystemIcon } from '../../../../Icon';
import { MenuListItemIcon } from '../../../../MenuList/MenuListItemIcon';
import { PermanentFilterButton } from '../view/PermanentFilterButton';
import { getHeader as getHeaderText } from '../../../utils/getNodeText';

export const ColumnSortPicker: FC<React.PropsWithChildren<{}>> = () => {
  const { intl } = useIntlUds();
  const { getHeaderGroups, setSorting: setSortBy } = useDataTableContext();
  const headerGroups = getHeaderGroups?.();
  const entries = headerGroups?.[0]?.headers?.filter((it) => it.column.columnDef.enableSorting);
  const currentSort = entries?.filter((it) => it.column.getIsSorted())?.[0];

  const clearSort = useCallback(() => {
    setSortBy?.([]);
  }, [setSortBy]);

  const setSort = useCallback(
    (column: Column<object>) => {
      const sorted = column.getIsSorted();
      if (!sorted) {
        setSortBy?.([{ id: column.id, desc: false }]);
      } else if (sorted === 'desc') {
        setSortBy?.([]);
      } else {
        setSortBy?.([{ id: column.id, desc: true }]);
      }
    },
    [setSortBy]
  );

  if (!entries?.length) return null;
  return (
    <Dropdown
      ButtonItem={() => (
        <PermanentFilterButton
          label={intl.DataTable.ColumnSortPicker.label}
          valueLabel={
            currentSort ? `${intl.DataTable.ColumnSortPicker.label} ${getHeaderText(currentSort?.column)}` : undefined
          }
          dataTest="permanent-filter-sort-picker"
          onDelete={() => clearSort()}
        />
      )}
    >
      <MenuList>
        <MenuListLabel>{intl.DataTable.ColumnSortPicker.label}</MenuListLabel>
        {entries.map((item) => {
          const selected = item.column.getIsSorted();
          return (
            <MenuListItem hasEndIcon key={item.id} onClick={() => setSort(item.column)} selected={!!selected}>
              <MenuListItemText primary={getHeaderText(item.column)} />
              <MenuListItemIcon>{selected && <SortIcon item={item} />}</MenuListItemIcon>
            </MenuListItem>
          );
        })}
      </MenuList>
    </Dropdown>
  );
};

function SortIcon({
  item,
  disabled,
  style
}: {
  item?: { isSorted?: boolean; isSortedDesc?: boolean; id?: string };
  style?: CSSProperties;
  disabled?: boolean;
}) {
  if (!item?.isSorted) {
    return null;
  }
  return item.isSortedDesc ? (
    <SystemIcon
      aria-label=""
      style={style}
      className="sort__button"
      name="arrow_downward"
      dataTest={`${item.id}-sort-down`}
      disabled={disabled}
      color={tokens.colorPrimary}
    />
  ) : (
    <SystemIcon
      aria-label=""
      style={style}
      className="sort__button"
      name="arrow_upward"
      dataTest={`${item.id}-sort-up`}
      disabled={disabled}
      color={tokens.colorPrimary}
    />
  );
}
